import { useState } from 'react';

// activeTab is gone from the signature along with the modal's tabs: history now
// lives on the Messages page, so there is nothing here to switch between and no
// step that has to wait for the user to do so.
function useMessagingTour(showPreview) {
    const [runTour, setRunTour] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    const CopyableCode = ({ text }) => {
        const [showCopied, setShowCopied] = useState(false);

        const handleCopy = async () => {
            try {
                await navigator.clipboard.writeText(text);
                setShowCopied(true);
                setTimeout(() => setShowCopied(false), 2000); // Hide after 2 seconds
            } catch (err) {
                console.error('Failed to copy:', err);
            }
        };

        return (
            <span style={{ position: 'relative', display: 'inline-block' }}>
                <code 
                    onClick={handleCopy}
                    className="px-2 py-1 bg-gray-100 rounded cursor-pointer hover:bg-gray-200 transition"
                    title="Click to copy"
                    style={{ 
                        fontFamily: 'monospace',
                        fontSize: '0.9em',
                        userSelect: 'all'
                    }}
                >
                    {text}
                </code>
                {showCopied && (
                    <span
                        style={{
                            position: 'absolute',
                            bottom: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginBottom: '4px',
                            padding: '4px 8px',
                            backgroundColor: '#10b981',
                            color: 'white',
                            fontSize: '0.75rem',
                            borderRadius: '4px',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                            zIndex: 1000,
                            pointerEvents: 'none',
                        }}
                    >
                        Copied!
                        <span
                            style={{
                                position: 'absolute',
                                top: '100%',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 0,
                                height: 0,
                                borderLeft: '4px solid transparent',
                                borderRight: '4px solid transparent',
                                borderTop: '4px solid #10b981',
                            }}
                        />
                    </span>
                )}
            </span>
        );
    };

    const steps = [
        {
            target: '#messaging-modal',
            title: 'Messaging System',
            content: 'This system allows you to send personalized SMS messages to your customers. You can create templates with variables and send bulk messages.',
            disableBeacon: true,
            placement: 'center',
        },
        {
            target: '#template-selector',
            title: 'Template Selector',
            content: 'Choose from saved templates or create a new one. This helps you reuse common message formats.',
            disableBeacon: true,
        },
        {
            target: '#message-template',
            title: 'Message Template',
            content: 'Type your message here. You can use variables from the right panel to personalize each message.',
            disableBeacon: true,
        },
        {
            target: '#live-preview',
            title: 'Live Preview',
            content: 'See how your message will look with actual customer data. The character count helps you stay within SMS limits (160 characters).',
            disableBeacon: true,
        },
        {
            target: '#variable-search',
            title: 'Search Variables',
            content: 'Search for specific variables to add to your message.',
            disableBeacon: true,
        },
        {
            target: '#variable-buttons',
            title: 'Variable Buttons',
            content: 'Click any variable button to insert it at your cursor position in the message template. Variables are color-coded by category: blue for vehicle details, green for sale details, and purple for customer details.',
            disableBeacon: true,
            placement: 'left',
        },
        {
            target: '#save-template-button',
            title: 'Save Template',
            content: 'Save your message template for future use. You can update existing templates or create new ones.',
            disableBeacon: true,
        },
        {
            target: '#preview-messages-button',
            title: 'Preview Messages',
            content: 'Click here to see how each message will look for all selected customers before sending.',
            disableBeacon: true,
        },
        {
            target: '#message-template',
            title: 'Compose a test message',
            content: (
                        <span>
                            Try typing <CopyableCode text="Dear " /> in the template.
                        </span>
                    ),
            disableBeacon: true,
            spotlightClicks: true,
            customIdentifier: 'type-dear-step',
        },
        {
            target: "#variable-buttons",
            title: 'Click to insert First Name',
            content: "Click the 'First Name' button from these to insert a place holder for the First Name at your cursor position in the message template.",
            disableBeacon: true,
            spotlightClicks: true,
            placement: 'left',
            customIdentifier: 'click-firstname-step',
        },
        {
            target: '#message-template',
            title: 'Continue message',
            content: (
                        <span>
                            Add <CopyableCode text=" your vehicle " /> in the template.
                        </span>
                    ),
            disableBeacon: true,
            spotlightClicks: true,
            customIdentifier: 'your-vehicle-step',
            placement: 'right',
        },
        {
            target: "#variable-buttons",
            title: 'Click to insert Number Plate',
            content: "Click the 'Number Plate' button from these to insert a place holder for the Number Plate at your cursor position in the message template.",
            disableBeacon: true,
            spotlightClicks: true,
            placement: 'left',
            customIdentifier: 'click-number-plate-step',
        },
        {
            target: '#template-and-preview',
            title: 'Finished Message',
            content: "I've completed the message for you! Compare the message template and preview to see how it looks.",
            disableBeacon: true,
            spotlightClicks: true,
            customIdentifier: 'complete-message-step',
            placement: 'right',
        },
        {
            target: '#live-preview',
            title: 'Preview message',
            content: "Notice how the preview replaces the placeholders with actual customer data. This helps you see exactly what each recipient will get.",
            disableBeacon: true,
            placement: 'right',
        },
        {
            target: '#preview-messages-button',
            title: 'Preview all messages',
            content: 'Click this button to see previews for all selected customers before sending.',
            disableBeacon: true,
            spotlightClicks: true,
            disableNext: !showPreview,
        },
        {
            target: '#message-preview-list',
            title: 'Message Previews',
            content: 'Here are the previews of the messages that will be sent to each customer. Review them to ensure everything looks good.',
            disableBeacon: true,
            placement: 'top-left',   
        },
        {
            target: '#send-messages-button',
            title: 'Send Messages',
            content: 'Once you are satisfied with the previews, click this button to send the messages to your customers. For this demo, we shall not send actual messages.',
            disableBeacon: true,
            spotlightClicks: false,
            
        },
        {
            target: '#back-to-edit-button',
            title: 'Back to Edit',
            content: 'If you need to make any changes, click this button to return to the message editor.',
            disableBeacon: true,
            spotlightClicks: false,
        },
        {
            target: '#back-to-edit-button',
            title: 'Click to return to Edit',
            content: 'Please click to return to edit mode so we can see how to save our template.',
            disableBeacon: true,
            spotlightClicks: true,
            disableNext: showPreview,            
        },
        {
            target: '#save-template-button',
            title: 'Save Template',
            content: "Finally, click this button to save your message template for future re-use, so you don't have to retype it.",
            disableBeacon: true,
            spotlightClicks: true,
        },
        {
            target: '#save-template-button',
            title: 'Enter Template Name and Save',
            content: "A dialog will appear prompting you to enter a name for your template. Please enter a name and click 'Save Template'.",
            disableBeacon: true,
            spotlightClicks: true,
        },
        {
            target: '#template-selector',
            title: 'Saved Templates',
            content: 'Once your template is saved, next time it will appear here, in the template selector drop-down list for future use.',
            disableBeacon: true,
        }
    ];

    const handleStartTutorial = () => {
        setRunTour(false);
        setStepIndex(0);
        
        setTimeout(() => {
            // Filter out steps whose elements don't exist in DOM
            const availableSteps = steps.filter(step => {
                const element = document.querySelector(step.target);
                return element !== null;
            });

            if (availableSteps.length === 0) {
                console.warn('No tour elements found in DOM');
                return;
            }

            setStepIndex(0);
            setRunTour(true);
        }, 100);
    };

    const handleJoyrideCallback = (data) => {
        const { status, action, type, index } = data;
        console.log("Joyride callback data: ", data);

        // Handle error status (when element not found)
        if (status === 'error') {
            console.warn('Tour error - element not found, trying next step');
            const nextIndex = index + 1;
            if (nextIndex < steps.length) {
                setStepIndex(nextIndex);
                return;
            } else {
                setRunTour(false);
                setStepIndex(0);
                return;
            }
        }

        // Handle all closing actions consistently
        if (status === 'finished' || status === 'skipped') {
            console.log("Tour ended with status:", status);
            setRunTour(false);
            setStepIndex(0);
            return;
        }
        
        // Treat 'close' action the same as 'stop'
        if (action === 'close' || action === 'stop') {
            console.log("Tour stopped/closed with action:", action);
            setRunTour(false);
            setStepIndex(0);
            return;
        }

        if (type === 'step:before' && action === 'next') {
            //If the next step's target is "#variable-button-FirstName" but the user
            //didn't enter "Dear " in the message template, go back to the previous step
            if (steps[index]?.target === "#variable-buttons" && steps[index]?.customIdentifier && steps[index]?.customIdentifier === 'click-firstname-step') {
                const messageTemplateElement = document.querySelector('#message-template');
                if (messageTemplateElement) {
                    const messageTemplateText = messageTemplateElement.value || messageTemplateElement.innerText || '';
                    if (!messageTemplateText.includes('Dear ')) {
                        alert('Please type "Dear " in the message template, including the space after "Dear" to proceed with the tour. .');
                        setStepIndex(index - 1); // Return to previous step
                        return;
                    }
                }
            }

            if (steps[index]?.target === "#message-template" && steps[index]?.customIdentifier && steps[index]?.customIdentifier === 'your-vehicle-step') {
                const messageTemplateElement = document.querySelector('#message-template');
                if (messageTemplateElement) {
                    const messageTemplateText = messageTemplateElement.value || messageTemplateElement.innerText || '';
                    if (!messageTemplateText.includes('{{FirstName}}')) {
                        alert('Please insert the First Name place holder to proceed.');
                        setStepIndex(index - 1); // Return to previous step
                        return;
                    }
                }
            }

            if (steps[index]?.target === "#variable-buttons" && steps[index]?.customIdentifier && steps[index]?.customIdentifier === 'click-number-plate-step') {
                const messageTemplateElement = document.querySelector('#message-template');
                if (messageTemplateElement) {
                    const messageTemplateText = messageTemplateElement.value || messageTemplateElement.innerText || '';
                    if (!messageTemplateText.includes(' your vehicle ')) {
                        alert('Please add " your vehicle " after the first name place holder to proceed.');
                        setStepIndex(index - 1); // Return to previous step
                        return;
                    }
                }
            }

            if (steps[index]?.target === "#template-and-preview" && steps[index]?.customIdentifier && steps[index]?.customIdentifier === 'complete-message-step') {
                const messageTemplateElement = document.querySelector('#message-template');
                if (messageTemplateElement) {
                    const messageTemplateText = messageTemplateElement.value || messageTemplateElement.innerText || '';
                    if (!messageTemplateText.includes('{{NumberPlate}}')) {
                        alert('Please insert the Number Plate place holder to proceed.');
                        setStepIndex(index - 1); // Return to previous step
                        return;
                    } else {
                        if (messageTemplateElement) {
                            const newValue = "Dear {{FirstName}}, your vehicle {{NumberPlate}} still owes {{TotalBalance}} and your last payment was on {{LastPmtDate}}, so kindly call us on 0752xxxxxx to resolve the matter. Thank you.";

                            // Create a setter reference from the element's prototype
                            const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
                                window.HTMLTextAreaElement.prototype,
                                "value"
                            ).set;

                            // Use that setter to set the value, so React sees it
                            nativeTextAreaValueSetter.call(messageTemplateElement, newValue);

                            // Dispatch an input event so React's onChange fires
                            const event = new Event("input", { bubbles: true });
                            messageTemplateElement.dispatchEvent(event);
                        }
                    }
                }
            }

        }        

            
        
        if (type === 'step:after' && action === 'next') {
            setStepIndex(index + 1);
        } else if (type === 'step:after' && action === 'prev') {
            let targetIndex = index - 1;

            if (targetIndex < 0) {
                setRunTour(false);
                setStepIndex(0);
                return;
            }

            setStepIndex(targetIndex);
        }
    };

    return {
        runTour,
        stepIndex,
        steps,
        handleStartTutorial,
        handleJoyrideCallback,
    };
}

export default useMessagingTour;